import type { OnboardingState } from './onboarding-api'

export type OnboardingRecommendation = {
  key: 'governance_owner' | 'known_ai_uses' | 'first_ai_use' | 'strategy'
  title: string
  path: '/workspace/setup' | '/ai-register' | '/strategy'
  reason: string
}

/**
 * Release 1 deterministic onboarding recommendation.
 *
 * Phase 2.2 deliberately removes the duplicate customer preference screen.
 * The recommendation is derived only from facts already recorded during setup.
 * Legacy preferredStart values remain historical data and do not influence the
 * current recommendation. Product selection is never treated as proof that an
 * AI capability or organisational AI use exists.
 */
export function getOnboardingRecommendation(
  state: OnboardingState | null | undefined,
): OnboardingRecommendation | null {
  if (!state) return null

  const governanceOwnerConfirmed = Boolean(state.governanceOwner && !state.governanceOwnerNeedsReassignment)
  const governanceOwnerPending = Boolean(state.governanceOwnerInvitation)

  if (!governanceOwnerConfirmed && !governanceOwnerPending) {
    return {
      key: 'governance_owner',
      title: 'Confirm governance responsibility',
      path: '/workspace/setup',
      reason: 'Swell needs a named person to coordinate the governance framework.',
    }
  }

  if (state.setup.aiRegisterStatus === 'yes') {
    return {
      key: 'known_ai_uses',
      title: 'Bring your known AI uses into Swell',
      path: '/ai-register',
      reason: 'You told us your organisation already keeps a useful list or register of AI use.',
    }
  }

  return {
    key: 'first_ai_use',
    title: 'Record your first AI use',
    path: '/ai-register',
    reason: 'Start with one product and business purpose. Swell will translate the plain-English answers into the governance information it needs.',
  }
}
